import {
  WEB_SOCKET_PING_INTERVAL,
  WEB_SOCKET_PING_MESSAGE,
} from 'config/constants';

/**
 * Opens the app websocket.
 *
 * A logged-in socket proves who it is with an "auth" message carrying the
 * access token (never in the URL, which ends up in server logs). The server
 * then puts it in the player's room and sends the current balance. The ?id=
 * is only kept so an API that predates the auth message keeps working.
 *
 * Keep-alive pings are answered with "pong" by the current API. Once a pong
 * has been seen, a socket that stays silent for three ping intervals is
 * treated as dead and reported closed, so the caller can reconnect instead of
 * waiting minutes for the browser to notice.
 */
export const createWebSocket = (
  userId?: number,
  getAccessToken?: () => string | undefined
): WebSocket => {
  const webSocketBaseUrl = process.env.REACT_APP_CHAT_SOCKET_BASE_URL ?? '';

  const query = typeof userId === 'number' ? `id=${userId}` : 'id=anon';
  const webSocket = new WebSocket(`${webSocketBaseUrl}${query}`);

  let serverAnswersPings = false;
  let lastMessageAt = Date.now();

  webSocket.addEventListener('open', () => {
    lastMessageAt = Date.now();
    const token = getAccessToken?.();
    if (token) {
      webSocket.send(JSON.stringify({ type: 'auth', token }));
    }
  });

  webSocket.addEventListener('message', (e: MessageEvent) => {
    lastMessageAt = Date.now();
    // Only a short message can be the pong: a live update is megabytes.
    if (
      !serverAnswersPings &&
      typeof e.data === 'string' &&
      e.data.length < 100
    ) {
      try {
        if (JSON.parse(e.data)?.type === 'pong') serverAnswersPings = true;
      } catch {
        // not JSON: not a pong
      }
    }
  });

  const interval = setInterval(() => {
    if (webSocket.readyState !== WebSocket.OPEN) return;
    const silentFor = Date.now() - lastMessageAt;
    if (serverAnswersPings && silentFor > WEB_SOCKET_PING_INTERVAL * 3) {
      clearInterval(interval);
      webSocket.close();
      // A dead link can take minutes to report its own close; say it now.
      webSocket.dispatchEvent(
        new CloseEvent('close', { code: 4000, reason: 'heartbeat timeout' })
      );
      return;
    }
    webSocket.send(WEB_SOCKET_PING_MESSAGE);
  }, WEB_SOCKET_PING_INTERVAL);

  webSocket.addEventListener('close', () => {
    clearInterval(interval);
  });

  return webSocket;
};

const sendLiveSubscription = (
  webSocket: WebSocket,
  type: 'subscribe_live' | 'unsubscribe_live',
  sportIds: number[]
) => {
  if (webSocket.readyState !== WebSocket.OPEN) {
    return;
  }
  webSocket.send(
    JSON.stringify({
      type,
      sport_ids: sportIds,
    })
  );
};

export const subscribeLiveSports = (
  webSocket: WebSocket,
  sportIds: number[]
) => {
  sendLiveSubscription(webSocket, 'subscribe_live', sportIds);
};

export const unsubscribeLiveSports = (
  webSocket: WebSocket,
  sportIds: number[]
) => {
  sendLiveSubscription(webSocket, 'unsubscribe_live', sportIds);
};

const sendPrematchSubscription = (
  webSocket: WebSocket,
  type: 'subscribe_prematch' | 'unsubscribe_prematch',
  sportIds: number[]
) => {
  if (webSocket.readyState !== WebSocket.OPEN) {
    return;
  }
  webSocket.send(
    JSON.stringify({
      type,
      sport_ids: sportIds,
    })
  );
};

export const subscribePrematchSports = (
  webSocket: WebSocket,
  sportIds: number[]
) => {
  sendPrematchSubscription(webSocket, 'subscribe_prematch', sportIds);
};

export const unsubscribePrematchSports = (
  webSocket: WebSocket,
  sportIds: number[]
) => {
  sendPrematchSubscription(webSocket, 'unsubscribe_prematch', sportIds);
};
